// FormFrame/fields/FieldText.tsx
import * as React from "react"
import { Input } from "@workspace/ui/components/shadcn/input"
import { Label } from "@workspace/ui/components/shadcn/label"
import { Button } from "@workspace/ui/components/shadcn/button"
import { Eye, EyeOff } from "lucide-react"
import type { FieldDef, FormApi } from "./fieldTypes"
import {
    normalizeString,
    getRequiredMark,
    getHelpText,
    getErrorText,
} from "./fieldUtils"
import { useEnforceInputValue } from "./useDomEnforce"
import { cn } from "@workspace/ui/lib/utils"

type TextDef = Extract<FieldDef, { type: "text" | "email" | "password" | "number" }>

type Props = {
    def: TextDef
    form: FormApi
    invalid: boolean
}

export function FieldText({ def, form, invalid }: Props) {
    const r = form.register(def.name)
    const value = normalizeString(r.value)
    const inputRef = React.useRef<HTMLInputElement>(null)

    useEnforceInputValue(inputRef, value)

    const IconStart = (def as any).iconStart
    const IconEnd = (def as any).iconEnd
    const [showPassword, setShowPassword] = React.useState(false)

    const typeAttr =
        def.type === "password"
            ? showPassword
                ? "text"
                : "password"
            : def.type

    const requiredMark = getRequiredMark(def)
    const helpText = getHelpText(def)
    const err = (form.formState.errors as any)?.[def.name]
    const errorText = getErrorText(err)

    const triggerValidate = () => {
        form.validateField?.(def.name)
        void form.validateFieldUnique?.(def.name)
    }

    return (
        <>
            <div className="ui-field-header">
                {def.label ? (
                    <Label htmlFor={def.name} className="ui-field-label">
                        {def.label}
                        {requiredMark}
                    </Label>
                ) : (
                    <span />
                )}
                {helpText ? (
                    <span className="ui-field-helptext">{helpText}</span>
                ) : (
                    <span />
                )}
            </div>

            <div className="relative w-full">
                {IconStart ? <IconStart className="ui-field-icon-start" /> : null}

                {/* Wrapper übernimmt Rand / Error / Focus */}
                <div
                    className={cn(
                        "ui-control h-9 w-full",
                        invalid && "ui-control-error",
                        !invalid &&
                        "focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/30"
                    )}
                >
                    <Input
                        ref={inputRef}
                        id={def.name}
                        type={typeAttr}
                        placeholder={def.placeholder}
                        disabled={def.disabled}
                        readOnly={def.readOnly}
                        className={cn(
                            "h-full w-full border-0 shadow-none bg-transparent",
                            IconStart && "pl-10",
                            (IconEnd || def.type === "password") && "pr-10"
                        )}
                        name={r.name}
                        value={value}
                        autoComplete="new-password"
                        onChange={(e: any) => {
                            r.onChange(e)
                            triggerValidate()
                        }}
                        onBlur={() => {
                            r.onBlur()
                            triggerValidate()
                        }}
                    />
                </div>

                {def.type === "password" ? (
                    <Button
                        type="button"
                        variant="ghost"
                        className="ui-field-icon-end h-6 w-6 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                        ) : (
                            <Eye className="w-4 h-4" />
                        )}
                    </Button>
                ) : null}

                {IconEnd && def.type !== "password" ? (
                    <IconEnd className="ui-field-icon-end" />
                ) : null}
            </div>

            <div className="ui-field-error">
                {errorText || null}
            </div>
        </>
    )
}
